import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { receiptPath, type BookingPage } from '../../api/transport'
import { amtcDialNumber, amtcDisplayNumber } from '../../lib/amtc'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { firstName } from './bookingWords'
import { downloadIcs } from './ics'

/**
 * The parts Screen 6 (M40-T09) and the booking page (T10) share, fed by the
 * booking page's answer (T08): Add to calendar, the receipt link, change or
 * cancel by phone, the status words, and the footer (Decision 2 statement 3).
 */

const action =
  'inline-flex min-h-[44px] items-center justify-center rounded-r3 border border-line-2 px-3 text-[13px] font-semibold text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

function DeskNumber(): ReactNode {
  const dial = amtcDialNumber()
  if (!dial) return 'the transport desk'
  return (
    <a
      href={`tel:${dial}`}
      className="font-semibold text-accent underline underline-offset-2"
    >
      {amtcDisplayNumber() ?? dial}
    </a>
  )
}

export function AddToCalendar({ page }: { page: BookingPage }): ReactNode {
  return (
    <button
      type="button"
      className={action}
      onClick={() =>
        downloadIcs({
          reference: page.reference,
          pickupAt: page.pickup_at,
          hospital: page.hospital,
          firstName: firstName(page),
          desk: amtcDisplayNumber() ?? amtcDialNumber(),
        })
      }
    >
      Add to calendar
    </button>
  )
}

export function ReceiptLink({
  reference,
  manageKey,
}: {
  reference: string
  manageKey: string
}): ReactNode {
  return (
    <Link to={receiptPath(reference, manageKey)} className={action}>
      View receipt
    </Link>
  )
}

/** "Book the same trip again" (M40-T11, owner Q11): a new booking from the
 * safety check, with the trip filled in and the date left to choose. */
export function BookAgain({ onClick }: { onClick: () => void }): ReactNode {
  return (
    <button type="button" className={action} onClick={onClick}>
      Book the same trip again
    </button>
  )
}

/** Self-service change or cancel is deferred (it needs refund calculation):
 * the desk does it, quoting the reference. */
export function ChangeOrCancel({
  reference,
}: {
  reference: string
}): ReactNode {
  return (
    <details className="rounded-r3 border border-line-1 px-3 py-2.5 text-[13px] text-ink-2">
      <summary className="cursor-pointer font-medium text-ink-1">
        Change or cancel
      </summary>
      <p className="mt-2">
        To change or cancel, call the transport desk
        {amtcDialNumber() ? ' on ' : ''}
        {amtcDialNumber() ? <DeskNumber /> : null} and quote {reference}.
      </p>
    </details>
  )
}

/** Decision 2 statement 3, at the foot of Screen 6 and the booking page. */
export function BookingFooter(): ReactNode {
  return (
    <footer className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12.5px] leading-relaxed text-ink-1">
      {amtcDialNumber() ? (
        <>
          Questions about this booking? Transport desk: <DeskNumber />.
        </>
      ) : (
        // No dead number, and no "Transport desk: the transport desk".
        'Questions about this booking? Call the transport desk.'
      )}{' '}
      Medical emergency? Call{' '}
      <a
        href={`tel:${EMERGENCY_NUMBER}`}
        className="font-semibold text-sos underline underline-offset-2"
      >
        {EMERGENCY_NUMBER}
      </a>{' '}
      — do not wait for this vehicle.
    </footer>
  )
}
